import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
// Components
import Header from '../header';
import Footer from '../footer';
import AlertBanner from '../alert-banner';
import InformationBanner from '../information-banner';
import { GlobalStyle } from '../theme/global';
import { Container } from '../styled/containers';
import Breadcrumbs from '../breadcrumbs';
import EditInContentful from './edit-in-contentful';
// Utils
import {
  getComponentOfType,
  containsComponentOfType,
} from '../../utils/contentful-components';
import { useSiteConfig } from '../../hooks/site-config';
import { usePageMeta } from '../page';
import LayoutContext from './context';
import { SafeTemplate } from '../error-boundaries';

const Layout = ({
  children,
  assemblies,
  heroContent,
  mainNavigation,
  showQuickExitButton = false,
}) => {
  const donationBannerAssembly = getComponentOfType(
    assemblies,
    'ContentfulComponentDonationBanner'
  );

  const {
    email,
    linkedIn,
    twitter,
    facebook,
    instagram,
    tiktok,
    footer,
    logo,
    logoMobile,
    siblingSiteName,
    siblingSiteUrl,
    alertBanner,
    informationBanner,
  } = useSiteConfig();

  const containsDonationHero =
    containsComponentOfType(
      heroContent,
      'ContentfulComponentDonationHeroWithButtons'
    ) ||
    containsComponentOfType(
      heroContent,
      'ContentfulComponentDonationHeroWithInput'
    );

  const { breadcrumbs, contentfulId, path } = usePageMeta();

  const layoutParams = useMemo(
    () => ({
      removeFooterMargin: donationBannerAssembly?.removeMarginBottom,
      containsDonationHero,
      showQuickExitButton,
    }),
    [containsDonationHero, donationBannerAssembly, showQuickExitButton]
  );

  const pathsToHideNavBarOn = [
    '/donate_to_shelter',
    '/donate_hv',
    '/donate/children_in_temporary_accommodation',
    '/donate/rough_sleeping',
    '/donate/renters_rights',
    '/donate/support_housing_research',
    '/donate/support_families_facing_eviction',
  ];
  const hideNavBar =
    process.env.SHELTER_SITE === 'england' &&
    pathsToHideNavBarOn.some((item) => item === path);

  return (
    <LayoutContext.Provider value={layoutParams}>
      <GlobalStyle />
      {alertBanner && (
        <AlertBanner
          bannerTitle={alertBanner.bannerTitle}
          text={alertBanner.text}
          position={alertBanner.position}
          contentfulId={alertBanner.contentful_id}
        />
      )}
      <Header
        navigation={mainNavigation}
        logo={logo}
        logoMobile={logoMobile}
        siblingSiteName={siblingSiteName}
        siblingSiteUrl={siblingSiteUrl}
        hideNavBar={hideNavBar}
      />
      {informationBanner && (
        <InformationBanner
          text={informationBanner.text}
          color={informationBanner.color}
          metadata={informationBanner.metadata}
        />
      )}
      {breadcrumbs && breadcrumbs.length > 2 && (
        <Container>
          <Breadcrumbs crumbs={breadcrumbs} />
        </Container>
      )}
      <main id="main">{children}</main>

      <Footer
        removeMarginTop={layoutParams.removeFooterMargin}
        linkedIn={linkedIn}
        twitter={twitter}
        facebook={facebook}
        tiktok={tiktok}
        instagram={instagram}
        email={email}
        logo={logo}
        {...footer}
      />

      {/* Use the GATSBY_IS_PREVIEW env var to only show the button on preview builds */}
      {process.env.PREVIEW === 'true' ? (
        <EditInContentful contentfulId={contentfulId} />
      ) : null}
    </LayoutContext.Provider>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  assemblies: PropTypes.arrayOf(PropTypes.object),
  heroContent: PropTypes.arrayOf(PropTypes.object),
  mainNavigation: PropTypes.object,
  path: PropTypes.string.isRequired,
  canonicalTag: PropTypes.string,
  showQuickExitButton: PropTypes.bool,
};

export default SafeTemplate(Layout);
